import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'

import { PageMeta } from '../../components/common/PageMeta'
import { CardSkeletonGrid, EmptyState, ErrorState, Section } from '../../components/common/States'
import { AttractionCard } from './AttractionCard'
import { contentApi } from '../../api/endpoints'
import { useLocale } from '../../i18n/LocaleProvider'
import type { AttractionCategory, Season } from '../../types/api'

const CATEGORIES: { value: AttractionCategory; icon: string }[] = [
  { value: 'NATURE', icon: '🌲' },
  { value: 'HISTORY', icon: '🏰' },
  { value: 'CULTURE', icon: '🎨' },
  { value: 'FOOD', icon: '🥧' },
  { value: 'FAMILY', icon: '👨‍👩‍👧' },
  { value: 'EVENT', icon: '🎭' },
  { value: 'SHOPPING', icon: '🛍️' },
]

const SEASONS: Season[] = ['WINTER', 'SPRING', 'SUMMER', 'AUTUMN']

export function ExplorePage() {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const [category, setCategory] = useState<AttractionCategory | undefined>()
  const [season, setSeason] = useState<Season | undefined>()

  const destinations = useQuery({
    queryKey: ['destinations', locale],
    queryFn: () => contentApi.destinations(locale),
  })

  const attractions = useQuery({
    queryKey: ['attractions', locale, category, season],
    queryFn: () => contentApi.attractions(locale, { category, season }),
  })

  return (
    <>
      <PageMeta title={t('explore.title')} description={t('explore.subtitle')} path="/explore" />

      <Section title={t('explore.title')} subtitle={t('explore.subtitle')}>
        {destinations.isPending && <CardSkeletonGrid count={5} height={190} />}
        {destinations.isError && (
          <ErrorState error={destinations.error} onRetry={() => void destinations.refetch()} />
        )}
        {destinations.data && (
          <Box
            sx={{
              display: 'grid',
              gap: 2.5,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            }}
          >
            {destinations.data.map((destination) => (
              <Card
                key={destination.id}
                component={RouterLink}
                to={`/explore/${destination.slug}`}
                sx={{
                  textDecoration: 'none',
                  display: 'block',
                  '&:hover': { boxShadow: 5, transform: 'translateY(-3px)' },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" spacing={1} sx={{ mb: 1.25, flexWrap: 'wrap', rowGap: 0.75 }}>
                    {destination.distanceKm != null && (
                      <Chip
                        size="small"
                        label={`🚗 ${t('explore.kmAway', { count: destination.distanceKm })}`}
                      />
                    )}
                    {destination.driveTimeMinutes != null && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`⏱ ${t('explore.driveTime', { count: destination.driveTimeMinutes })}`}
                      />
                    )}
                    {destination.gatewayCity && (
                      <Chip size="small" color="secondary" label={`✈️ ${t('explore.gatewayCity')}`} />
                    )}
                  </Stack>

                  <Typography variant="h3" component="h2" color="text.primary" sx={{ mb: 1 }}>
                    {destination.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {destination.summary}
                  </Typography>
                  <Typography variant="caption" color="primary.main">
                    {t('explore.attractionCount', { count: destination.attractionCount })} →
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Section>

      <Box sx={{ bgcolor: 'background.paper' }}>
        <Section title={t('explore.title')} subtitle={t('activities.subtitle')}>
          <Stack spacing={1.5} sx={{ mb: 3.5 }}>
            <FilterRow
              label={t('explore.filterCategory')}
              options={[
                { value: undefined, label: t('common.all'), icon: '✳️' },
                ...CATEGORIES.map((c) => ({ value: c.value, label: c.value, icon: c.icon })),
              ]}
              selected={category}
              onSelect={(value) => setCategory(value as AttractionCategory | undefined)}
            />
            <FilterRow
              label={t('explore.filterSeason')}
              options={[
                { value: undefined, label: t('common.all'), icon: '📅' },
                ...SEASONS.map((s) => ({ value: s, label: t(`activities.${s}`), icon: seasonIcon(s) })),
              ]}
              selected={season}
              onSelect={(value) => setSeason(value as Season | undefined)}
            />
          </Stack>

          {attractions.isPending && <CardSkeletonGrid count={6} />}
          {attractions.isError && (
            <ErrorState error={attractions.error} onRetry={() => void attractions.refetch()} />
          )}
          {attractions.data?.length === 0 && (
            <EmptyState icon="🔍" title={t('explore.noResults')} />
          )}
          {attractions.data && attractions.data.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              }}
            >
              {attractions.data.map((attraction) => (
                <AttractionCard key={attraction.id} attraction={attraction} showDestination />
              ))}
            </Box>
          )}
        </Section>
      </Box>
    </>
  )
}

function seasonIcon(season: Season): string {
  return { WINTER: '❄️', SPRING: '🌱', SUMMER: '☀️', AUTUMN: '🍂', ALL_YEAR: '📅' }[season]
}

function FilterRow<T extends string | undefined>({
  label,
  options,
  selected,
  onSelect,
}: {
  label: string
  options: { value: T; label: string; icon: string }[]
  selected: T
  onSelect: (value: T) => void
}) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        {options.map((option) => (
          <Chip
            key={option.value ?? 'all'}
            label={`${option.icon} ${option.label}`}
            onClick={() => onSelect(option.value)}
            color={selected === option.value ? 'primary' : 'default'}
            variant={selected === option.value ? 'filled' : 'outlined'}
          />
        ))}
      </Stack>
    </Box>
  )
}
